import React from 'react';

import { Paper, Button, Tooltip } from '@mui/material';
import { observer } from 'mobx-react';

import classes from './ChallengeProfileCard.module.scss';
import { ChallengeProfileInfo } from '../../../../interfaces/ChallengeProfileInfo';
import { Point } from '../../../../../shared/components/ui-components';
import { ChallengeStatus } from './ChallengeInfo/ChallengeStatus';
import { loggedInUserDataStore } from '../../../../../shared/stores/loggedInUserDataStore';
import { challengeProfileStore } from '../../../../stores/challengeProfileStore';
import { openConfirmationModal } from '../../../../../shared/features/confirmation';
import { pluralize } from '../../../../../shared/utils/stringUtils';
import { getPublishTooltip } from './utils/getPublishTooltip';

type ChallengeProfileCardProps = {
  challengeId: number;
  challengeInfo: ChallengeProfileInfo;
};

const ChallengeProfileCard = observer(
  ({ challengeId, challengeInfo }: ChallengeProfileCardProps) => {
    const { isEntity } = loggedInUserDataStore;
    const { challengeName, participantCount, status, endDate, startDate } = challengeInfo;
    const {
      statusFlags,
      invalidSections,
      getIsEditing,
      publishChallenge,
      unpublishChallenge,
    } = challengeProfileStore;
    const isEditing = getIsEditing();
    const hasInvalidSections = invalidSections.length > 0;
    const publishTooltip = getPublishTooltip(invalidSections);

    const showPublishButton = isEntity && statusFlags.DRAFT;

    const showUnpublishButton =
      isEntity && (statusFlags.PUBLISHED || statusFlags.ACTIVE) && participantCount === 0;

    const challengeLink = statusFlags.DRAFT
      ? `#!/challenge-preview/${challengeId}`
      : `#!/challenge-landing/${challengeId}`;

    const handlePublish = () => {
      openConfirmationModal({
        title: 'Are you sure you want to publish the challenge?',
        text: 'The challenge will be visible to customers, but they can only join once registration opens. You will be able to unpublish this challenge, and make changes, up until your first customer registers.',
        confirmBtnConfig: {
          text: 'Confirm',
          onClick: () => publishChallenge(challengeId),
        },
      });
    };

    const handleUnpublish = () => {
      openConfirmationModal({
        title: 'Are you sure you want to unpublish the challenge?',
        text: 'All important dates will be removed/cleared/ deleted',
        confirmBtnConfig: {
          text: 'Confirm',
          onClick: () => unpublishChallenge(challengeId),
        },
      });
    };

    return (
      <Paper variant="roundedBox" className={classes.container}>
        <div className={classes.infoSection}>
          <h1 className={classes.title}>{challengeName}</h1>
          <div className={classes.metaInfo}>
            <span className={classes.participants}>
              {pluralize(participantCount, 'participant', 's')}
            </span>
            <Point />
            <ChallengeStatus status={status} startDate={startDate} endDate={endDate} />
          </div>
        </div>
        <div className={classes.actionsSection}>
          <Button
            variant="outlined"
            size="medium"
            color="secondary"
            href={challengeLink}
            target="_blank"
          >
            {statusFlags.DRAFT ? 'Preview Challenge' : 'Open Challenge'}
          </Button>
          <Button
            variant="outlined"
            size="medium"
            color="secondary"
            href={`#!/challenge-leaderboard/${challengeId}`}
            target="_blank"
          >
            Leaderboard
          </Button>
          {showPublishButton && (
            <Tooltip
              title={publishTooltip}
              placement="top"
              classes={{ tooltip: classes.publishTooltip }}
            >
              <span>
                <Button
                  variant="contained"
                  size="medium"
                  color="primary"
                  disabled={isEditing || hasInvalidSections}
                  onClick={handlePublish}
                >
                  Publish
                </Button>
              </span>
            </Tooltip>
          )}
          {showUnpublishButton && (
            <Button
              variant="contained"
              size="medium"
              color="primary"
              disabled={isEditing}
              onClick={handleUnpublish}
            >
              Unpublish
            </Button>
          )}
        </div>
      </Paper>
    );
  },
);

export { ChallengeProfileCard };
